import TextField from "@material-ui/core/TextField";
import { ChangeEvent, FocusEvent, useState } from "react";
import useAccountForm from "./../../../SignIn/utils";
import Checkbox from "components/formElements/Checkbox/Checkbox";
import { PasswordStatus } from "components/PasswordRequirement";
import { signUp, signUpIndividual } from "services/authUtil";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { getErrorsString } from "services/util";
import { Link } from "react-router-dom";
import TalaeraRoutes from "components/routes/TalaeraRoutes";
import { PasswordInput } from "../../../../components/PasswordInput/PasswordInput";
import { useNavigate, useParams } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import LinkedinButton from "components/AuthButtons/LinkedInButton";
import MetaButton from "components/AuthButtons/MetaButton";
import GoogleButton from "components/AuthButtons/GoogleButton";
import { OAuthProvider, OAuthStep } from "model/oauth";
import { isEnumMember } from "utils/enums";
import EmailButton from "components/AuthButtons/EmailButton";
import "./CreateAccount.css";
import OrDivider from "components/auth/components/OrDivider";

type CreateAccountProps = {
  onCreateAccount: (email: string, resend_verification_token: string) => void;
  isPro: boolean;
  selectedLanguage: string;
};

// eslint-disable-next-line no-shadow
enum CreateAccountMethod {
  Google = "google",
  Linkedin = "linkedin",
  Meta = "meta",
  Email = "email",
}

const createMethods = [
  {
    label: "Signup with Email",
    method: CreateAccountMethod.Email,
    component: EmailButton,
  },
  {
    label: "Signup with Google",
    method: CreateAccountMethod.Google,
    component: GoogleButton,
  },
  {
    label: "Signup with LinkedIn",
    method: CreateAccountMethod.Linkedin,
    component: LinkedinButton,
  },
  {
    label: "Signup with Facebook",
    method: CreateAccountMethod.Meta,
    component: MetaButton,
  },
];

const initData = {
  firstName: "",
  email: "",
  password: "",
  confirmPassword: "",
};

export const CreateAccount = ({
  onCreateAccount,
  isPro,
  selectedLanguage,
}: CreateAccountProps) => {
  const { code } = useParams() as { code?: string };
  const {
    account,
    setAccount,
    errors,
    touched,
    passwordStatus,
    setTouched,
    onValidate,
    onTouched,
  } = useAccountForm({
    ...initData,
    ...(code && {
      lastName: "",
    }),
  });
  const [createMethod, setCreateMethod] = useState<CreateAccountMethod>();
  const [loading, setLoading] = useState(false);
  const [createErrors, setCreateErrors] = useState("");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCreateDisabled = Object.keys(errors).length > 0;

  const handleSelectMethod = (method: CreateAccountMethod) => {
    if (method === "email") {
      setCreateMethod(method);
    } else if (isEnumMember(OAuthProvider, method as string)) {
      navigate(
        TalaeraRoutes.buildSignInOAuthPath(method, OAuthStep.Start, { isPro, code })
      );
    }
  };

  const handleChangeAccountData = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleCreate = (evt: any) => {
    evt.preventDefault();

    const isValidated = onValidate();
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      countryCode: true,
      primaryLanguage: true,
      password: true,
      confirmPassword: true,
      location: true,
    });

    if (isValidated) {
      setLoading(true);
      let payload = {
        ...account,
        localization_lang: selectedLanguage,
      } as { [key: string]: any };

      if (isPro) {
        payload.isPro = true;
      }

      if (code) {
        payload.pricing_model_code = code;

        payload.place = {
          address: account.location?.address,
          timezoneId: account.location?.timezoneId,
        };

        signUp(
          payload,
          (resp: {
            sso?: { redirect_url: string };
            resend_verification_token: string;
          }) => {
            if (resp.sso?.redirect_url) {
              window.location.replace(resp.sso.redirect_url);
              return;
            }
            if (account.email) {
              onCreateAccount(account.email, resp.resend_verification_token);
              setLoading(false);
            }
          },
          (respBody: any) => {
            setLoading(false);
            setCreateErrors(respBody);
          }
        );
        return;
      }

      signUpIndividual(
        payload,
        (resp: { resend_verification_token: string }) => {
          if (account.email) {
            onCreateAccount(account.email, resp.resend_verification_token);
            setLoading(false);
          }
        },
        (respBody: any) => {
          setLoading(false);
          setCreateErrors(respBody);
        }
      );
    }
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const getCreateErrorComponents = (error: string) => {
    let errorString = getErrorsString(error);
    let isLoginLink = errorString.includes("Log in here");

    if (isLoginLink) {
      errorString = errorString.replace("Log in here.", "");
    }

    return (
      <div className="sign-up-error">
        {errorString}
        {isLoginLink && (
          <>
            <br />
            <Link to={TalaeraRoutes.SIGN_IN.path}>{t("Signup.Log in here.")}</Link>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="create-account-step">
      <h1 className="step-title">{t("Signup.Create an account")}</h1>

      {!createMethod && (
        <div className="create-account-methods">
          {createMethods.map((method, index) => (
            <>
              <method.component
                key={method.method}
                onClick={() => handleSelectMethod(method.method)}
              >
                {method.label}
              </method.component>
              {index === 0 && <OrDivider key={"divider"} />}
            </>
          ))}
        </div>
      )}

      {createMethod === CreateAccountMethod.Email && (
        <>
          <form onSubmit={handleCreate}>
            <div className="create-account-form">
              {createErrors && getCreateErrorComponents(createErrors)}

              <div className="form-wrapper">
                <TextField
                  className="full-width"
                  name="firstName"
                  label={t("Signup.First Name")}
                  variant="outlined"
                  value={account.firstName}
                  error={!!(errors.firstName && touched.firstName)}
                  helperText={touched.firstName && errors.firstName}
                  onBlur={handleBlurField}
                  onChange={handleChangeAccountData}
                />
                {code && (
                  <TextField
                    className="full-width"
                    name="lastName"
                    label={t("Signup.Last Name")}
                    variant="outlined"
                    value={account.lastName}
                    error={!!(errors.lastName && touched.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    onBlur={handleBlurField}
                    onChange={handleChangeAccountData}
                  />
                )}
                <TextField
                  className="full-width"
                  name="email"
                  label={t("Signup.Email Address")}
                  variant="outlined"
                  value={account.email}
                  error={!!(errors.email && touched.email)}
                  helperText={touched.email && errors.email}
                  onBlur={handleBlurField}
                  onChange={handleChangeAccountData}
                />
                <PasswordInput
                  className="full-width"
                  name="password"
                  label={t("Signup.Password")}
                  variant="outlined"
                  type="password"
                  value={account.password}
                  error={!!(errors.password && touched.password)}
                  helperText={touched.password && errors.password}
                  onBlur={handleBlurField}
                  onChange={handleChangeAccountData}
                />
                <PasswordInput
                  className="full-width"
                  name="confirmPassword"
                  label={t("Signup.Confirm Password")}
                  variant="outlined"
                  type="password"
                  value={account.confirmPassword}
                  error={!!(errors.confirmPassword && touched.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  onBlur={handleBlurField}
                  onChange={handleChangeAccountData}
                />
              </div>

              {account.password && (
                <div className="password-confirm-panel">
                  <div>{t("Common.Password requirements")}:</div>
                  <div className="password-confirm-list">
                    <Checkbox
                      isChecked={passwordStatus.includes(PasswordStatus.Lowercase)}
                      label={t("Common.lower case letter")}
                    />
                    <Checkbox
                      isChecked={passwordStatus.includes(PasswordStatus.Uppercase)}
                      label={t("Common.upper case letter")}
                    />
                    <Checkbox
                      isChecked={passwordStatus.includes(PasswordStatus.NumberOrSymbol)}
                      label={t("Common.number or symbol")}
                    />
                    <Checkbox
                      isChecked={passwordStatus.includes(PasswordStatus.MinimumLength)}
                      label={t("Common.at least 8 characters")}
                    />
                  </div>
                </div>
              )}

              <div className="legal-panel">
                <p>
                  <Trans i18nKey={"Signup.byJoining"}>
                    By joining Talaera, you agree to Talaera's
                    <br />
                    <a
                      href="https://www.talaera.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://www.talaera.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>
                  </Trans>
                </p>
              </div>

              <div className="step-action">
                {!loading ? (
                  <button
                    disabled={isCreateDisabled}
                    className="btn full-width"
                    type="submit"
                  >
                    {t("Signup.Create Account")}
                  </button>
                ) : (
                  <TalaeraSpinner />
                )}
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
